import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
var _excluded = ["key", "dataIndex", "children"];
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SettingOutlined, VerticalAlignBottomOutlined, VerticalAlignMiddleOutlined, VerticalAlignTopOutlined } from '@ant-design/icons';
import { useIntl } from '@ant-design/pro-provider';
import { runFunction, useRefFunction } from '@ant-design/pro-utils';
import { Checkbox, ConfigProvider, Popover, Space, Tooltip, Tree } from 'antd';
import classNames from 'classnames';
import omit from 'omit.js';
import { useContext, useEffect, useMemo, useRef } from 'react';
import Container from '../../container';
import { genColumnKey } from '../../utils/index';
import { useStyle } from './style';

var ToolTipIcon = function ToolTipIcon(_ref) {
  var title = _ref.title,
      show = _ref.show,
      children = _ref.children,
      columnKey = _ref.columnKey,
      fixed = _ref.fixed;

  var _Container$useContain = Container.useContainer(),
      columnsMap = _Container$useContain.columnsMap,
      setColumnsMap = _Container$useContain.setColumnsMap;

  if (!show) {
    return null;
  }

  return _jsx(Tooltip, {
    title: title,
    children: _jsx("span", {
      onClick: function onClick(e) {
        e.stopPropagation();
        e.preventDefault();
        var config = columnsMap[columnKey] || {};
        var disableIcon = typeof config.disable === 'boolean' && config.disable;
        if (disableIcon) return;

        var columnKeyMap = _objectSpread(_objectSpread({}, columnsMap), {}, _defineProperty({}, columnKey, _objectSpread(_objectSpread({}, config), {}, {
          fixed: fixed
        })));

        setColumnsMap(columnKeyMap);
      },
      children: children
    })
  });
};

var CheckboxListItem = function CheckboxListItem(_ref2) {
  var columnKey = _ref2.columnKey,
      isLeaf = _ref2.isLeaf,
      title = _ref2.title,
      className = _ref2.className,
      fixed = _ref2.fixed;
  var intl = useIntl();

  var dom = _jsxs("span", {
    className: "".concat(className, "-list-item-option"),
    children: [_jsx(ToolTipIcon, {
      columnKey: columnKey,
      fixed: "left",
      title: intl.getMessage('tableToolBar.leftPin', '固定在列首'),
      show: fixed !== 'left',
      children: _jsx(VerticalAlignTopOutlined, {})
    }), _jsx(ToolTipIcon, {
      columnKey: columnKey,
      fixed: undefined,
      title: intl.getMessage('tableToolBar.noPin', '不固定'),
      show: !!fixed,
      children: _jsx(VerticalAlignMiddleOutlined, {})
    }), _jsx(ToolTipIcon, {
      columnKey: columnKey,
      fixed: "right",
      title: intl.getMessage('tableToolBar.rightPin', '固定在列尾'),
      show: fixed !== 'right',
      children: _jsx(VerticalAlignBottomOutlined, {})
    })]
  });

  return _jsxs("span", {
    className: "".concat(className, "-list-item"),
    children: [_jsx("div", {
      className: "".concat(className, "-list-item-title"),
      children: title
    }), !isLeaf ? dom : null]
  }, columnKey);
};

var CheckboxList = function CheckboxList(_ref3) {
  var _treeDataConfig$list, _treeDataConfig$list2;

  var list = _ref3.list,
      draggable = _ref3.draggable,
      checkable = _ref3.checkable,
      className = _ref3.className,
      _ref3$showTitle = _ref3.showTitle,
      showTitle = _ref3$showTitle === void 0 ? true : _ref3$showTitle,
      listTitle = _ref3.title,
      _ref3$listHeight = _ref3.listHeight,
      listHeight = _ref3$listHeight === void 0 ? 280 : _ref3$listHeight;

  var _Container$useContain2 = Container.useContainer(),
      columnsMap = _Container$useContain2.columnsMap,
      setColumnsMap = _Container$useContain2.setColumnsMap,
      sortKeyColumns = _Container$useContain2.sortKeyColumns,
      setSortKeyColumns = _Container$useContain2.setSortKeyColumns;

  var show = list && list.length > 0;
  var treeDataConfig = useMemo(function () {
    if (!show) return {};
    var checkedKeys = [];

    var loopData = function loopData(data, parentConfig) {
      return data.map(function (_ref4) {
        var _config$disable;

        var key = _ref4.key,
            dataIndex = _ref4.dataIndex,
            children = _ref4.children,
            rest = _objectWithoutProperties(_ref4, _excluded);

        var columnKey = genColumnKey(key, rest.index);
        var config = columnsMap[columnKey || 'null'] || {
          show: true
        };

        if (config.show !== false && (parentConfig === null || parentConfig === void 0 ? void 0 : parentConfig.show) !== false && !children) {
          checkedKeys.push(columnKey);
        }

        var item = _objectSpread(_objectSpread({
          key: columnKey
        }, omit(rest, ['className'])), {}, {
          selectable: false,
          disabled: config.disable === true,
          disableCheckbox: typeof config.disable === 'boolean' ? config.disable : (_config$disable = config.disable) === null || _config$disable === void 0 ? void 0 : _config$disable.checkbox,
          isLeaf: parentConfig ? true : undefined
        });

        if (children) {
          item.children = loopData(children, config);
        }

        return item;
      });
    };

    return {
      list: loopData(list),
      keys: checkedKeys
    };
  }, [columnsMap, list, show]);
  /** 移动到指定的位置 */

  var move = useRefFunction(function (id, targetId, dropPosition) {
    var newMap = _objectSpread({}, columnsMap);

    var newColumns = _toConsumableArray(sortKeyColumns);

    var findIndex = newColumns.findIndex(function (columnKey) {
      return columnKey === id;
    });
    var targetIndex = newColumns.findIndex(function (columnKey) {
      return columnKey === targetId;
    });
    var isDownWord = dropPosition > targetIndex;
    if (findIndex < 0) return;
    var targetItem = newColumns[findIndex];
    newColumns.splice(findIndex, 1);

    if (dropPosition === 0) {
      newColumns.unshift(targetItem);
    } else {
      newColumns.splice(isDownWord ? targetIndex : targetIndex + 1, 0, targetItem);
    } // 重新生成排序数组


    newColumns.forEach(function (key, order) {
      newMap[key] = _objectSpread(_objectSpread({}, newMap[key] || {}), {}, {
        order: order
      });
    }); // 更新数组

    setColumnsMap(newMap);
    setSortKeyColumns(newColumns);
  });
  /** 选中反选功能 */

  var onCheckTree = useRefFunction(function (e) {
    var columnKey = e.node.key;

    var newSetting = _objectSpread({}, columnsMap[columnKey]);

    newSetting.show = e.checked;
    setColumnsMap(_objectSpread(_objectSpread({}, columnsMap), {}, _defineProperty({}, columnKey, newSetting)));
  });

  if (!show) {
    return null;
  }

  var listDom = _jsx(Tree, {
    itemHeight: 24,
    draggable: draggable && !!((_treeDataConfig$list = treeDataConfig.list) === null || _treeDataConfig$list === void 0 ? void 0 : _treeDataConfig$list.length) && ((_treeDataConfig$list2 = treeDataConfig.list) === null || _treeDataConfig$list2 === void 0 ? void 0 : _treeDataConfig$list2.length) > 1,
    checkable: checkable,
    onDrop: function onDrop(info) {
      var dropKey = info.node.key;
      var dragKey = info.dragNode.key;
      var dropPosition = info.dropPosition,
          dropToGap = info.dropToGap;
      var position = dropPosition === -1 || !dropToGap ? dropPosition + 1 : dropPosition;
      move(dragKey, dropKey, position);
    },
    blockNode: true,
    onCheck: function onCheck(_, e) {
      return onCheckTree(e);
    },
    checkedKeys: treeDataConfig.keys,
    showLine: false,
    titleRender: function titleRender(_node) {
      var node = _objectSpread(_objectSpread({}, _node), {}, {
        children: undefined
      });

      return _jsx(CheckboxListItem, _objectSpread(_objectSpread({
        className: className
      }, node), {}, {
        title: runFunction(node.title, node),
        columnKey: node.key
      }));
    },
    height: listHeight,
    treeData: treeDataConfig.list
  });

  return _jsxs(_Fragment, {
    children: [showTitle && _jsx("span", {
      className: "".concat(className, "-list-title"),
      children: listTitle
    }), listDom]
  });
};

var GroupCheckboxList = function GroupCheckboxList(_ref5) {
  var localColumns = _ref5.localColumns,
      className = _ref5.className,
      draggable = _ref5.draggable,
      checkable = _ref5.checkable,
      listsHeight = _ref5.listsHeight;
  var rightList = [];
  var leftList = [];
  var list = [];
  var intl = useIntl();
  localColumns.forEach(function (item) {
    /** 不在 setting 中展示的 */
    if (item.hideInSetting) {
      return;
    }

    var fixed = item.fixed;

    if (fixed === 'left') {
      leftList.push(item);
      return;
    }

    if (fixed === 'right') {
      rightList.push(item);
      return;
    }

    list.push(item);
  });
  var showRight = rightList && rightList.length > 0;
  var showLeft = leftList && leftList.length > 0;
  return _jsxs("div", {
    className: classNames("".concat(className, "-list"), _defineProperty({}, "".concat(className, "-list-group"), showRight || showLeft)),
    children: [_jsx(CheckboxList, {
      title: intl.getMessage('tableToolBar.leftFixedTitle', '固定在左侧'),
      list: leftList,
      draggable: draggable,
      checkable: checkable,
      className: className,
      listHeight: listsHeight
    }), _jsx(CheckboxList, {
      list: list,
      draggable: draggable,
      checkable: checkable,
      title: intl.getMessage('tableToolBar.noFixedTitle', '不固定'),
      showTitle: showLeft || showRight,
      className: className,
      listHeight: listsHeight
    }), _jsx(CheckboxList, {
      title: intl.getMessage('tableToolBar.rightFixedTitle', '固定在右侧'),
      list: rightList,
      draggable: draggable,
      checkable: checkable,
      className: className,
      listHeight: listsHeight
    })]
  });
};

function ColumnSetting(props) {
  var _props$checkable, _props$draggable;

  var columnRef = useRef({});
  var counter = Container.useContainer();
  var localColumns = props.columns;
  var _props$checkedReset = props.checkedReset,
      checkedReset = _props$checkedReset === void 0 ? true : _props$checkedReset;
  var columnsMap = counter.columnsMap,
      setColumnsMap = counter.setColumnsMap,
      clearPersistenceStorage = counter.clearPersistenceStorage;
  useEffect(function () {
    var _counter$propsRef$cur, _counter$propsRef$cur2;

    if ((_counter$propsRef$cur = counter.propsRef.current) === null || _counter$propsRef$cur === void 0 ? void 0 : (_counter$propsRef$cur2 = _counter$propsRef$cur.columnsState) === null || _counter$propsRef$cur2 === void 0 ? void 0 : _counter$propsRef$cur2.value) {
      var _counter$propsRef$cur3, _counter$propsRef$cur4;

      columnRef.current = JSON.parse(JSON.stringify(((_counter$propsRef$cur3 = counter.propsRef.current) === null || _counter$propsRef$cur3 === void 0 ? void 0 : (_counter$propsRef$cur4 = _counter$propsRef$cur3.columnsState) === null || _counter$propsRef$cur4 === void 0 ? void 0 : _counter$propsRef$cur4.value) || {}));
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  /**
   * 设置全部选中，或全部未选中
   *
   * @param show
   */

  var setAllSelectAction = useRefFunction(function () {
    var show = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    var columnKeyMap = {};

    var loopColumns = function loopColumns(columns) {
      columns.forEach(function (_ref6) {
        var key = _ref6.key,
            fixed = _ref6.fixed,
            index = _ref6.index,
            children = _ref6.children;
        var columnKey = genColumnKey(key, index);

        if (columnKey) {
          columnKeyMap[columnKey] = {
            show: show,
            fixed: fixed
          };
        }

        if (children) {
          loopColumns(children);
        }
      });
    };

    loopColumns(localColumns);
    setColumnsMap(columnKeyMap);
  });
  /** 全选和反选 */

  var checkedAll = useRefFunction(function (e) {
    if (e.target.checked) {
      setAllSelectAction();
    } else {
      setAllSelectAction(false);
    }
  });
  /** 重置项目 */

  var clearClick = useRefFunction(function () {
    clearPersistenceStorage === null || clearPersistenceStorage === void 0 ? void 0 : clearPersistenceStorage();
    setColumnsMap(columnRef.current);
  }); // 未选中的 key 列表

  var unCheckedKeys = Object.values(columnsMap).filter(function (value) {
    return !value || value.show === false;
  }); // 是否已经选中

  var indeterminate = unCheckedKeys.length > 0 && unCheckedKeys.length !== localColumns.length;
  var intl = useIntl();

  var _useContext = useContext(ConfigProvider.ConfigContext),
      getPrefixCls = _useContext.getPrefixCls;

  var className = getPrefixCls('pro-table-column-setting');

  var _useStyle = useStyle(className),
      wrapSSR = _useStyle.wrapSSR;

  return wrapSSR(_jsx(Popover, {
    arrowPointAtCenter: true,
    title: _jsxs("div", {
      className: "".concat(className, "-title"),
      children: [_jsx(Checkbox, {
        indeterminate: indeterminate,
        checked: unCheckedKeys.length === 0 && unCheckedKeys.length !== localColumns.length,
        onChange: function onChange(e) {
          return checkedAll(e);
        },
        children: intl.getMessage('tableToolBar.columnDisplay', '列展示')
      }), checkedReset ? _jsx("a", {
        onClick: clearClick,
        className: "".concat(className, "-action-rest-button"),
        children: intl.getMessage('tableToolBar.reset', '重置')
      }) : null, (props === null || props === void 0 ? void 0 : props.extra) ? _jsx(Space, {
        size: 12,
        align: "center",
        children: props.extra
      }) : null]
    }),
    overlayClassName: "".concat(className, "-overlay"),
    trigger: "click",
    placement: "bottomRight",
    content: _jsx(GroupCheckboxList, {
      checkable: (_props$checkable = props.checkable) !== null && _props$checkable !== void 0 ? _props$checkable : true,
      draggable: (_props$draggable = props.draggable) !== null && _props$draggable !== void 0 ? _props$draggable : true,
      className: className,
      localColumns: localColumns,
      listsHeight: props.listsHeight
    }),
    children: props.children || _jsx(Tooltip, {
      title: intl.getMessage('tableToolBar.columnSetting', '列设置'),
      children: _jsx(SettingOutlined, {})
    })
  }));
}

export default ColumnSetting;